// @flow
import * as React from 'react';
import { ProductSection, ContentSection, SubmitButton, Select } from '../';
import CardAlert from '../Common/CardAlert';
import { ModerationState, Product, Observation, Catalog } from '../../models';
import { BrandAttribution, RetailerAttribution } from '../ClientAttribution';
import {
  clientModerationCard as cardCls,
  clientModerationCardMain as mainCls,
  clientModerationCardAside as asideCls,
  clientModerationCardOverlay,
} from './ClientModerationCard.scss';
import SectionSeparator from './SectionSeparator';
import { NoObservationsBadge, ObservationTimestamp } from './ObservationBadges';
import { CustomObservationInfoTooltip } from './CustomObservationInfoTooltip';
import collectionDiff from './collectionDiff';
import classnames from 'classnames';
import {
  getLinkedNativeUgcByIdUrl,
  requestReviewUpdateHandler,
  getCMObservation,
  getLatestClientObservation,
  getClientObservationLabels,
  getPwrObservationsLabels,
  mapObservationToOption,
  PowerReviewsObservationBadges,
  ClientObservationBadges,
  SiteStatusChangeNotice,
} from '../ModerationCard/ClientModerationCard/utils.js';
import RequestReviewModal from '../ModerationCard/ClientModerationCard/RequestReviewModal';

type Props = {
  product: Product,
  ugc: Object,
  moderationState: ModerationState,
  clientId: Id,
  onChange: ModerationState => void,
  onSiteStatusChange: ({
    moderationState: ModerationState,
    desiredPublished: boolean,
  }) => void,
  status: RequestStatus,
  onSubmit: (ModerationState, Id) => void,
  children?: React.Node,
  asideContent?: () => React.Node,
  authToken: string,
  shouldEnableUgcEditing: boolean,
};

type State = {
  showRequestReviewModal: boolean,
  requestReviewResponseMessage: ?string,
  reviewUpdateSuccessful: ?boolean,
  reviewRequestDate: ?string,
  reviewUpdateRequestPending: boolean,
};

function renderBlocks(children) {
  return React.Children.map(children, (child, ...args) => {
    return child ? [<SectionSeparator />, child] : null;
  });
}

export const ClientModerationCardSection = ({
  children,
  last,
  first,
  className,
}: {
  children: React.Node,
  first?: ?boolean,
  last?: boolean,
  className?: string,
}) => (
  <div
    className={classnames(
      `pa4 b--moon-gray bw1 mv0${className ? ' ' + className : ''}`,
      {
        bt: first !== true,
      }
    )}
  >
    {children}
  </div>
);

class ClientModerationCard extends React.Component<Props, State> {
  static defaultProps = {
    product: new Product(),
  };

  state = {
    showRequestReviewModal: false,
    requestReviewResponseMessage: '',
    reviewUpdateSuccessful: null,
    reviewRequestDate: this.props.ugc.ugc_edit_request_date
      ? this.props.ugc.ugc_edit_request_date
      : null,
    reviewUpdateRequestPending: false,
  };

  static BeforeMeta = (props: { children: React.Node }) =>
    renderBlocks(props.children);

  static AfterMeta = (props: { children: React.Node }) =>
    renderBlocks(props.children);

  static Aside = ({
    label,
    children,
    first,
  }: {
    label?: string,
    children: React.Node,
    first?: ?boolean,
  }) => (
    <ClientModerationCardSection first={first}>
      {label && <h3 className="dark-gray ma0 mb2 brandon">{label}</h3>}
      {children}
    </ClientModerationCardSection>
  );

  static ContentSection = (props: { children: React.Node }) => props.children;

  handleObservationChange = (options: Array<*>) => {
    const {
      moderationState: currentState,
      clientId,
      onChange,
      product,
    } = this.props;
    const currentSelectedCodes = ModerationState.getActionableObservations(
      currentState,
      clientId
    )
      .filter(Observation.isSelected)
      .map(o => o.observation_type_code);
    const pendingSelectedCodes = options.map(v => v.value);
    const { added, deleted } = collectionDiff(
      currentSelectedCodes,
      pendingSelectedCodes
    );
    const nextModerationState = currentState.withMutations(state => {
      added.forEach(code =>
        ModerationState.setObservation(state, clientId, code)
      );
      deleted.forEach(code =>
        ModerationState.removeObservation(state, clientId, code)
      );
    });

    onChange &&
      onChange(nextModerationState, {
        clientId,
        ugcClientId: product.merchant.group_id,
        ugctype: this.props.ugc.ugctype,
      });
  };

  handleSiteStatusChange = (option: *) => {
    const { value: desiredPublished } = option;

    const { moderationState, onSiteStatusChange, product } = this.props;

    onSiteStatusChange &&
      onSiteStatusChange(
        { moderationState, desiredPublished },
        {
          clientId: this.props.clientId,
          ugcClientId: product.merchant.group_id,
          ugctype: this.props.ugc.ugctype,
        }
      );
  };

  handleModerationStateSubmit = () => {
    const { onSubmit, moderationState, clientId } = this.props;
    const stateToSubmit = ModerationState.applyClientModeration(
      moderationState,
      clientId
    );
    onSubmit(stateToSubmit, clientId);
  };

  toggleRequestReviewModal = () =>
    this.setState(prevState => ({
      showRequestReviewModal: !prevState.showRequestReviewModal,
    }));

  sendReviewUpdateRequest = () => {
    this.toggleRequestReviewModal();
  };

  handleReviewUpdateRequest = (requestText: string) => {
    const { ugc, clientId, authToken: token } = this.props;
    const body = {
      client_id: clientId,
      ugc_id: ugc.ugc_id,
      request_text: requestText,
    };
    this.setState({ reviewUpdateRequestPending: true });

    requestReviewUpdateHandler(token, body).then(response => {
      this.setState({ reviewUpdateRequestPending: false });

      if (response.code >= 200 && response.code < 300) {
        this.setState({
          reviewUpdateSuccessful: true,
          reviewRequestDate: response.responseBody.updated_date,
        });
      } else {
        this.setState({
          reviewUpdateSuccessful: false,
          requestReviewResponseMessage: response.message,
        });
      }
    });
  };

  render() {
    const {
      moderationState,
      product,
      ugc,
      status,
      clientId,
      shouldEnableUgcEditing,
      children,
    } = this.props;

    const { reviewRequestDate } = this.state;
    const ugcId = moderationState.getUGCId();
    const subjectCatalog = product.merchant;
    const isSyndicated = subjectCatalog.group_id !== clientId;
    const pending = status === 'pending';
    const moderationStateHasNotChanged = ModerationState.isEqual(
      moderationState,
      clientId
    );

    const cmObservation = getCMObservation(moderationState, clientId);
    const isClientModerated = cmObservation != null;
    const lastClientObservation = isClientModerated
      ? getLatestClientObservation(moderationState, clientId)
      : undefined;
    const pmObservation = ModerationState.getSavedObservations(
      moderationState,
      ModerationState.PowerReviewsModerationContext
    ).get(Observation.PowerModerationCode);
    const fraudObservation = ModerationState.getSavedObservations(
      moderationState,
      ModerationState.PowerReviewsModerationContext
    ).get(Observation.Fraud);

    const observations = ModerationState.getActionableObservations(
      moderationState,
      clientId
    );
    const savedClientObservations = getClientObservationLabels(
      moderationState,
      clientId
    );
    const powerReviewsObservations = getPwrObservationsLabels(moderationState);
    const prObservationsWithCustomObservation = powerReviewsObservations.filter(
      o => o.custom_observation === true
    );

    const observationSelectOptions = observations.map(mapObservationToOption);
    const observationSelectValue = observations
      .filter(Observation.isSelected)
      .map(mapObservationToOption);
    const isBrandContent =
      ugc.catalog &&
      ugc.catalog !== Catalog.Empty &&
      ugc.catalog.group_id !== this.props.clientId;

    const beforeMeta = [];
    const afterMeta = [];
    const asideContent = [];
    let contentSection = null;

    const {
      /* 'editable' indicates whether a client can request a review update on this ugc.
       * to be editable, a review must have a headline, comment, rating, location, email, author, be native content,
       * a review or media first, and be published (published === true).
       */
      editable,
      old_ugc_id,
      new_ugc_id,
    } = ugc;

    const formattedRequestDate = reviewRequestDate
      ? new Date(reviewRequestDate).toDateString()
      : '';

    const shouldDisableRequestButton =
      reviewRequestDate || editable === false ? true : false;

    const isOldUgc = new_ugc_id ? true : false;
    const isNewUgc = old_ugc_id ? true : false;
    const isNativeContent = clientId === product.merchant.group_id;

    isSyndicated &&
      asideContent.push(
        <ClientModerationCardSection>
          <RetailerAttribution
            clientId={subjectCatalog.group_id}
            clientName={subjectCatalog.name}
            ugctype={ugc.ugctype}
          />
        </ClientModerationCardSection>
      );
    isBrandContent &&
      asideContent.push(
        <ClientModerationCardSection>
          <BrandAttribution
            clientId={ugc.catalog.group_id}
            clientName={ugc.catalog.name}
            ugctype={ugc.ugctype}
          />
        </ClientModerationCardSection>
      );
    React.Children.forEach(children, child => {
      if (child && child.type === ClientModerationCard.BeforeMeta) {
        beforeMeta.push(child);
      } else if (child && child.type === ClientModerationCard.Aside) {
        asideContent.push(child);
      } else if (child && child.type === ClientModerationCard.ContentSection) {
        contentSection = child;
      } else {
        afterMeta.push(child);
      }
    });
    const isCurrentPublished = ModerationState.isCurrentPublished(
      moderationState,
      clientId
    );
    const isSavedPublished = ModerationState.isSavedPublished(
      moderationState,
      clientId
    );

    return (
      <div className={cardCls}>
        {pending && <div className={clientModerationCardOverlay} />}
        <div className={mainCls}>
          <ProductSection
            model={product}
            key={product.subject_id}
            brandName
            categoryName
          />
          <div className="mb4">
            <SectionSeparator />
          </div>

          {isOldUgc && isNativeContent && (
            <CardAlert>
              This review has been updated.{' '}
              <a
                href={getLinkedNativeUgcByIdUrl(new_ugc_id, clientId)}
                target="_blank"
              >
                Read Updated Review
              </a>
            </CardAlert>
          )}

          {isNewUgc && isNativeContent && (
            <CardAlert>
              This is an update to a previous review.{' '}
              <a
                href={getLinkedNativeUgcByIdUrl(old_ugc_id, clientId)}
                target="_blank"
              >
                Read Original
              </a>
            </CardAlert>
          )}

          {contentSection ? (
            contentSection
          ) : (
            <ContentSection key={`cc-${ugcId}`} model={ugc} program="client">
              {beforeMeta}
            </ContentSection>
          )}
          {afterMeta}
        </div>
        <div className={asideCls} data-translate={true}>
          {React.Children.map(asideContent, (el, index) =>
            index === 0 ? React.cloneElement(el, { first: true }) : el
          )}
          <ClientModerationCardSection>
            <h3 className="dark-gray ma0 mb1 brandon">
              PowerReviews Observations
              {prObservationsWithCustomObservation.length > 0 && (
                <CustomObservationInfoTooltip />
              )}
            </h3>
            <PowerReviewsObservationBadges
              pmObservation={pmObservation}
              fraudObservation={fraudObservation}
              observations={powerReviewsObservations}
            />
            <br />
            <h3 className="dark-gray ma0 mb1 brandon">Add Observations</h3>
            {lastClientObservation ? (
              <ObservationTimestamp
                observation={lastClientObservation}
                showUserInfo
                recent
              />
            ) : (
              NoObservationsBadge('Not Reviewed by Your Team')
            )}
            {isClientModerated && (
              <ClientObservationBadges
                isClientModerated={isClientModerated}
                observations={savedClientObservations}
              />
            )}
            <Select
              name="observations"
              options={observationSelectOptions}
              value={observationSelectValue}
              onChange={this.handleObservationChange}
              autoSize={false}
              multi={true}
            />

            <div className="mv2">
              <SubmitButton
                label="Save Observations"
                pending={pending}
                onClick={this.handleModerationStateSubmit}
                disabled={moderationStateHasNotChanged && isClientModerated}
                className="f5 o-80 ph3 w-100"
              />
              <SiteStatusChangeNotice
                isCurrentPublished={isCurrentPublished}
                isSavedPublished={isSavedPublished}
                observationsHaveChanged={!moderationStateHasNotChanged}
              />
            </div>
          </ClientModerationCardSection>

          {editable && shouldEnableUgcEditing && (
            <ClientModerationCardSection className="bn">
              <section
                className="center tc mt0 mh"
                style={{ fontFamily: 'BrandonText' }}
              >
                <div className="w-100 left-0">
                  {reviewRequestDate && (
                    <div className="gray dib tc mb2 w-100">
                      <span className="mb0">
                        Update Requested on: {formattedRequestDate}
                      </span>
                    </div>
                  )}

                  <button
                    onClick={this.toggleRequestReviewModal}
                    className={` ${
                      shouldDisableRequestButton ? 'o-40' : 'pointer'
                    } f5 o-80 ph3 blue bg-white w-100 br2 pv3 ph3 bn`}
                    disabled={shouldDisableRequestButton}
                  >
                    Request Review Update
                  </button>
                </div>
              </section>

              <RequestReviewModal
                isVisible={this.state.showRequestReviewModal}
                handleVisibilityToggle={this.toggleRequestReviewModal}
                userName={ugc.author}
                userEmail={ugc.email}
                requestReviewUpdate={this.handleReviewUpdateRequest}
                requestReviewResponseMessage={
                  this.state.requestReviewResponseMessage
                }
                reviewUpdateSuccessful={this.state.reviewUpdateSuccessful}
                requestPending={this.state.reviewUpdateRequestPending}
              />
            </ClientModerationCardSection>
          )}
        </div>
      </div>
    );
  }
}

export default ClientModerationCard;



// WEBPACK FOOTER //
// ./src/components/ModerationCard/ClientModerationCard.js